import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (c) => `€${(c / 100).toLocaleString("en-US")}`;

export default function Account() {
  const { user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      api
        .get("/orders/mine")
        .then(({ data }) => setOrders(data))
        .catch(() => setOrders([]));
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      toast.success(mode === "login" ? "Welcome back." : "Welcome to the inner circle.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (user === null)
    return <div className="pt-40 container-lux label-mono text-mineral">Loading…</div>;

  // Authenticated customer view
  if (user) {
    return (
      <div className="pt-32 md:pt-40 pb-24 container-lux" data-testid="account-dashboard">
        <Reveal>
          <div className="flex flex-wrap justify-between items-end gap-6 mb-16 border-b border-mineral/20 pb-10">
            <div>
              <p className="label-mono mb-4">Your Account</p>
              <h1 className="heading-serif text-4xl md:text-5xl tracking-wider">{user.name}</h1>
              <p className="label-mono text-mineral mt-3">{user.email}</p>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="btn-relic border-mineral/40 text-alabaster hover:border-amethyst hover:text-amethyst"
            >
              Sign Out
            </button>
          </div>
        </Reveal>

        <p className="label-mono mb-8">Your Orders</p>
        {orders.length === 0 ? (
          <p className="text-alabaster/60">You have not placed an order yet.</p>
        ) : (
          <div className="space-y-6">
            {orders.map((o) => (
              <div
                key={o.session_id}
                className="border border-mineral/20 p-6 flex flex-wrap justify-between gap-4"
                data-testid="order-row"
              >
                <div>
                  <p className="font-mono text-xs text-bronze mb-2">
                    BATCH {o.session_id.slice(-8).toUpperCase()}
                  </p>
                  <p className="font-serif text-lg">
                    {o.items.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-xl">{eur(o.amount)}</p>
                  <p className="label-mono text-amethyst mt-1">Confirmed</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Auth form
  return (
    <div className="pt-32 md:pt-40 pb-24 container-lux max-w-md" data-testid="auth-page">
      <Reveal>
        <p className="label-mono mb-6">{mode === "login" ? "Welcome back" : "Join the house"}</p>
        <h1 className="heading-serif text-4xl md:text-5xl tracking-wider mb-10">
          {mode === "login" ? "Sign In" : "Create Account"}
        </h1>
        <form onSubmit={submit} className="space-y-7">
          {mode === "register" && (
            <div>
              <label className="label-mono block mb-2">Name</label>
              <input
                value={form.name}
                onChange={set("name")}
                data-testid="auth-name-input"
                className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst"
              />
            </div>
          )}
          <div>
            <label className="label-mono block mb-2">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="auth-email-input"
              className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst"
            />
          </div>
          <div>
            <label className="label-mono block mb-2">Password</label>
            <input
              required
              type="password"
              value={form.password}
              onChange={set("password")}
              data-testid="auth-password-input"
              className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="btn-relic w-full border-alabaster bg-alabaster text-charcoal hover:bg-amethyst hover:border-amethyst disabled:opacity-50"
          >
            {loading ? "…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="auth-toggle"
          className="label-mono text-amethyst mt-8 hover:text-bronze"
        >
          {mode === "login" ? "No account? Create one →" : "Already a keeper? Sign in →"}
        </button>
      </Reveal>
    </div>
  );
}
